<template>
    <DatePicker
        ref="datepicker"
        v-model="field.date"
        :attributes="field.attributes"
        :locale="field.locale"
        :max-date="maxDate"
        :min-date="minDate"
        :disabled-dates="disabledDates"
        :update-on-input="false"
    >
        <template v-slot="{ inputValue, hidePopover, showPopover }">
            <input
                @focusin="showPopover()"
                @focusout="hidePopover()"
                @keydown="onInputKeyDown($event, showPopover)"
                @keyup="updateValueByInput"
                :value="inputValue"
                maxlength="10"
            />
        </template>
    </DatePicker>
</template>

<script lang="ts" setup>
import AppDate from '@/classes/AppDate';
import {
  reactive,
  computed,
  watch,
  ref,
} from 'vue';

import { DatePicker } from 'v-calendar';
import 'v-calendar/dist/style.css';

const modelValue = defineModel();
const props = defineProps<{
  value?: string | number | Date,
  minDate?: string | number | Date,
  maxDate?: string | number | Date,
  disableWeekend?: boolean,
}>();
const datepicker = ref();
let defaultValue = undefined as Date | undefined;
if (props.value !== undefined && `${props.value || ''}`.trim() !== '') {
  defaultValue = new AppDate(props.value);
}

const field = reactive({
  date: defaultValue,
  dateText: '',
  attributes: [{
    timezone: 'Europe/Amsterdam',
  }],
  locale: 'de',
});

const formattedDateValue = computed(() => {
  if (!field.date) return null;

  return new AppDate(field.date).format('Y-m-d');
});

const emitInput = () => {
  modelValue.value = formattedDateValue.value;
};

watch(() => field.date, () => {
  emitInput();
});

const onInputKeyDown = (event: KeyboardEvent, callback: unknown) => {
  if (['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '.'].indexOf(event.key) >= 0) {
    if (typeof callback === 'function') {
      callback();
    }
  }
};

const getDatepickerInputDate = (value: string) => {
  const datePartials = value.split('.');
  const validDateValue = datePartials.length === 3
      && (datePartials[0].length === 1 || datePartials[0].length === 2)
      && (datePartials[1].length === 1 || datePartials[1].length === 2)
      && datePartials[2].length === 4;

  if (validDateValue) {
    return new Date(
      parseInt(datePartials[2], 10),
      parseInt(datePartials[1], 10) - 1,
      parseInt(datePartials[0], 10),
    );
  }

  return undefined;
};

const updateValueByInput = (e: any) => {
  const inputDate = getDatepickerInputDate(e?.target?.value);

  if (datepicker.value && inputDate !== undefined) {
    datepicker.value.updateValue(inputDate);
    datepicker.value.move(inputDate);
    datepicker.value.hidePopover();
  }
};

const disabledDates = computed(() => {
  if (props.disableWeekend) {
    return {
      weekdays: [
        1,
        7,
      ],
    };
  }

  return null;
});
</script>
